import { signMessage } from '@wagmi/core'
import { signMessage as signMessageThirdweb } from 'thirdweb/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useActiveAccount } from 'thirdweb/react'
import {
  useAccount,
  useConnect,
  useDisconnect,
  useNetwork,
  useSwitchNetwork
} from 'wagmi'
import constants from 'app/constants'
import tagManager from 'app/tagManager'
import useLogger from 'lib/util/useLogger'
import useThirdweb from './useThirdweb'

const debug = useLogger('Use Wallet', '#F08080')

/**
 * The use wallet
 *
 * @returns {object} the context object
 */
function useWallet () {
  const accountWagmi = useAccount()
  const account = useActiveAccount()
  const { connect: connectWagmi, connectors } = useConnect()
  const { disconnect: disconnectWagmi } = useDisconnect()
  const { chain: activeChain } = useNetwork()
  const { connectThirdweb, disconnectThirdweb, getAccountThirdweb } =
    useThirdweb()
  const [accountThirdweb, setAccountThirdwebState] = useState(null)
  const accountThirdwebRef = useRef(null)
  const [wallet, setWalletState] = useState(null)
  const walletRef = useRef(null)
  const accountWagmiRef = useRef(null)
  const network = useSwitchNetwork({
    chainId: constants.CHAIN
  })

  const setWallet = useCallback(props => {
    walletRef.current = props
    setWalletState(walletRef.current)
  }, [])

  /**
   * Set the thirdweb account
   */
  const setAccountThirdweb = useCallback(
    props => {
      accountThirdwebRef.current = props

      setAccountThirdwebState(accountThirdwebRef.current)
    },
    [account]
  )

  /**
   * Refresh the thirdweb account
   *
   * @param {object} info - The thirdweb account info
   */
  const refreshThirdwebAccount = async info => {
    if (!constants.IS_THIRDWEB_ENABLED) {
      // Thirdweb is disabled, prevent the sign in
      return
    }

    let thirdwebInfo = info

    if (!info) {
      thirdwebInfo = await getAccountThirdweb()
    }

    debug.log('refreshThirdwebAccount() => thirdwebInfo', thirdwebInfo)

    if (thirdwebInfo) {
      setAccountThirdweb({
        provider: 'inApp',
        address: thirdwebInfo.address,
        authDetails: thirdwebInfo,
        wallet: {
          address: thirdwebInfo.address,
          provider: 'inApp'
        }
      })

      return
    }

    setAccountThirdweb(null)
  }

  /**
   * Connect
   */
  const connect = useCallback(
    async provider => {
      if (provider === 'inApp') {
        const response = await connectThirdweb()

        if (response) {
          debug.log('connect() => response', response)

          const account = await response?.getAccount()

          debug.log('connect() => account', account)

          setTimeout(async () => {
            await refreshThirdwebAccount(account)
          }, 1000)

          return
        }
      }

      const connector = connectors.find(
        connector => connector?.id === provider
      )

      if (!connector) {
        console.error('Connector with ID not found:', provider)

        return
      }

      connectWagmi({ connector })
    },
    [connectThirdweb, refreshThirdwebAccount, connectors, connectWagmi]
  )

  /**
   * Disconnect
   */
  debug.log('accountThirdwebRef.current', accountThirdwebRef.current)

  const disconnect = useCallback(async () => {
    try {
      if (accountThirdwebRef.current) {
        await disconnectThirdweb()
        accountThirdwebRef.current = null

        setAccountThirdweb(null)

        return
      }

      debug.log('disconnect')

      disconnectWagmi()
    } catch (error) {
      console.log(error)
    }
  }, [disconnectThirdweb, disconnectWagmi])

  /**
   * On detect wrong network, disconnect and request switch
   */
  const onWrongNetwork = useCallback(() => {
    disconnect()
    network.switchNetwork?.(constants.CHAIN)
  }, [network])

  /**
   * Effect: When the `accountWagmi` changes
   */
  useEffect(() => {
    accountWagmiRef.current = accountWagmi
  }, [accountWagmi])

  /**
   * Effect: When account details & chain info changes
   */
  useEffect(() => {
    if (accountThirdweb) {
      // Found a thirdweb account, use that
      setWallet(accountThirdweb)
      tagManager.setWalletAddress(accountThirdweb?.address || null)

      return
    }

    if (!accountWagmi?.address || !accountWagmi?.connector) {
      setWallet(null)
      tagManager.setWalletAddress(null)

      return
    }

    if (activeChain?.id !== constants.CHAIN) {
      onWrongNetwork()

      return
    }

    const nextWallet = {
      provider: 'wagmi',
      address: accountWagmi?.address
    }

    if (JSON.stringify(nextWallet) === JSON.stringify(walletRef.current)) {
      return
    }

    tagManager.setWalletAddress(nextWallet.address)
    setWallet(nextWallet)
  }, [accountThirdweb, accountWagmi, activeChain, onWrongNetwork, setWallet])

  /**
   * Get the signer based on the type of connection in use (thirdweb vs wagmi)
   */
  const getSigner = useCallback(
    async () => {
      if (walletRef.current?.provider === 'inApp') {
        // Use Thirdweb v5 signMessage API
        return {
          signMessage: async ({ message }) => {
            if (!account) {
              throw new Error('No active Thirdweb account')
            }

            try {
              // Use the standalone signMessage function from thirdweb/utils (v5 API)
              const signature = await signMessageThirdweb({
                message,
                account
              })

              console.log('Message signed with Thirdweb:', signature)

              return signature
            } catch (error) {
              console.error('Error signing message with Thirdweb:', error)

              throw error
            }
          },
          useCache: false
        }
      }

      if (walletRef.current?.provider === 'wagmi') {
        return {
          useCache: true,
          signMessage
        }
      }

      return null
    },
    [account]
  )

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      connect,
      disconnect,
      getSigner,
      wallet,
      refreshThirdwebAccount
    }),
    [connect, disconnect, wallet, getSigner, refreshThirdwebAccount]
  )

  return ctx
}

export default useWallet
